/**
 * Builds the signed-in user from the server's /auth/validate response.
 *
 * The server is the single source of truth for a user's identity. It reads the
 * email off the token exactly as it does when stamping `assigned_to` on a
 * ticket, so a user built from this response always matches the tickets they
 * own. Deriving the email in the browser instead (from an MSAL account, whose
 * `username` is the UPN) breaks that agreement for anyone whose UPN differs
 * from their mail address, and silently locks them out of their own ticket.
 *
 * @param {object} validateResponse - Response body from authAPI.validateToken()
 * @param {object} [account] - MSAL account, used only for a display-name fallback
 * @returns {object} The user object held in auth state
 * @throws {Error} If the server returned no email — a placeholder would break
 *                 every ownership check downstream, so fail loudly instead.
 */
export const buildAuthUser = (validateResponse, account = null) => {
  const serverUser = validateResponse?.user

  if (!serverUser?.email) {
    throw new Error('Authentication failed: the server did not return an email for this account.')
  }

  return {
    email: serverUser.email,
    name: serverUser.name || account?.name || serverUser.email,
    roles: serverUser.roles || [],
    application_roles: serverUser.application_roles || [],
    preferred_language: serverUser.preferred_language || 'en',
  }
}

export default buildAuthUser
